export default class AdSlot {
    constructor({ id, element, type, location, service, controls = [] }) {
        this.id = id;
        this.element = element;
        this.type = type;
        this.location = location;
        this.service = service;
        this.controls = controls;
        this.loaded = false;
        this.adData = null;
    }

    // Initialize the slot - now handled by AdService with intersection observer
    init() {
        // Initialization is now handled by AdService with intersection observer
        // This method is kept for backward compatibility
        
        // Also try to load immediately if we have data
        if (this.canBeLoaded()) {
            this.load();
        }
    }

    // Load ad into the slot
    async load(adData = null) {
        if (this.loaded) {
            return;
        }

        // Use provided ad data or try to find it
        this.adData = adData || this.findAdData();

        if (!this.adData) {
            return;
        }

        this.loaded = true;

        // Clear any existing content including skeleton loader
        this.element.innerHTML = '';

        // Check if element is still in DOM
        if (!document.body.contains(this.element)) {
            return;
        }

        // Ensure Cordless Media manager is initialized before rendering
        this.initializeCordlessMediaManager();

        await this.renderAd();
    }

    // Find ad data from service or Vuex store
    findAdData() {
        // Check service first
        if (this.service && this.service.ads && this.service.ads[this.id]) {
            return this.service.ads[this.id];
        }

        // Check the dedicated ads store using getters
        if (window.store && window.store.getters) {
            // First try to get by exact ID match
            const exactMatch = window.store.getters['ads/getAdById'](this.id);
            if (exactMatch) {
                return exactMatch;
            }
            
            // Then try to find an ad for this location
            const adsForLocation = window.store.getters['ads/adsByLocation'](this.location);
            
            // If this slot has the exact ID as an ad, use it
            const directMatch = adsForLocation.find(ad => ad.id === this.id);
            if (directMatch) {
                return directMatch;
            }
            
            // For other cases, try to parse HTML to find matching ID (legacy fallback)
            const htmlMatch = adsForLocation.find(ad => {
                if (!ad.html) return false;
                const match = ad.html.match(/id=['"]([^'"]+)['"]/); 
                return match && match[1] === this.id;
            });
            
            if (htmlMatch) {
                return htmlMatch;
            }
        }

        return null;
    }

    // Render the ad based on its type
    async renderAd() {
        if (!this.adData) return;

        const { type, html, callback, image, componentName, props } = this.adData;

        try {
            switch (type) {
                case 'html':
                    this.element.innerHTML = html;
                    break;

                case 'image':
                    this.element.innerHTML = `<img src="${image}" alt="Advertisement" style="width:100%; height:auto;">`;
                    break;

                case 'script':
                    if (html) {
                        this.element.innerHTML = html;
                    }
                    if (callback) {
                        await this.injectCallback(callback);
                    }
                    break;

                case 'component':
                    if (componentName && window.Vue) {
                        const Component = window.Vue.component(componentName);
                        if (Component) {
                            new Component({ propsData: props || {} }).$mount(this.element);
                        }
                    }
                    break;

                default:
                    this.element.innerHTML = html || '';
                    if (callback) {
                        await this.injectCallback(callback);
                    }
            }
        } catch (error) {
            console.error(`AdSlot: Error rendering ad for ${this.id}:`, error);
            this.element.innerHTML = '<div class="ad-error">Failed to load advertisement</div>';
        }
    }

    // Inject callback script with error handling
    async injectCallback(callback) {
        // Check if callback was already injected for this ad
        const callbackKey = `adCallbackInjected_${this.id}`;
        if (window[callbackKey]) {
            return;
        }

        let processedCallback = this.processCallbackTemplate(callback);

        // Special handling for Cordless Media ads to prevent multiple requestUnits calls
        if (this.isCordlessMediaCallback(processedCallback)) {
            processedCallback = this.handleCordlessMediaCallback(processedCallback);
        }

        // Check if this callback contains script injection patterns before processing

                // Handle callbacks that contain script injection patterns
        if (processedCallback.includes('createElement') || processedCallback.includes('insertBefore') || processedCallback.includes('appendChild')) {
            // For complex callbacks with script injection, use the original callback as-is
            // The script loading will be handled by the external systems
            processedCallback = this.processCallbackTemplate(callback);
        } else if (processedCallback.includes('cmWrapper') && !this.isCordlessMediaCallback(processedCallback)) {
            // Only clean simple cmWrapper references that don't involve script injection
            processedCallback = `
                (function() {
                    function waitForCmWrapper(callback, maxAttempts = 50) {
                        if (window.cmWrapper) {
                            callback();
                        } else if (maxAttempts > 0) {
                            setTimeout(function() {
                                waitForCmWrapper(callback, maxAttempts - 1);
                            }, 100);
                        } else {
                            console.warn('AdSlot: cmWrapper not available for callback execution');
                        }
                    }
                    
                    waitForCmWrapper(function() {
                        try {
                            ${processedCallback}
                        } catch (error) {
                            console.error('AdSlot: Error executing callback:', error);
                        }
                    });
                })();
            `;
        }

        // Remove any existing callback script for this ad
        const existingScript = document.querySelector(`script[data-ad-slot="${this.id}"]`);
        if (existingScript) {
            existingScript.remove();
        }

        // Create and inject new script
        const script = document.createElement('script');
        script.type = 'text/javascript';
        script.setAttribute('data-ad-slot', this.id);
        
        try {
            script.text = processedCallback;
        } catch (error) {
            console.error(`AdSlot: Error setting script text for ${this.id}:`, error);
            console.error(`AdSlot: Problematic callback:`, processedCallback);
            return; // Don't inject malformed script
        }

        // Add error handling
        script.onerror = (error) => {
            console.error(`AdSlot: Callback script error for ${this.id}:`, error);
        };

        try {
            document.head.appendChild(script);
        } catch (error) {
            console.error(`AdSlot: Error appending script for ${this.id}:`, error);
        }

        // Mark callback as injected
        window[callbackKey] = true;
    }

    // Check if this is a Cordless Media callback
    isCordlessMediaCallback(callback) {
        return callback.includes('cmWrapper.ads.defineUnit') && callback.includes('cmWrapper.ads.requestUnits');
    }

    // Handle Cordless Media callback to prevent multiple requestUnits calls
    handleCordlessMediaCallback(callback) {
        // Initialize global Cordless Media management if not already done
        this.initializeCordlessMediaManager();

        // Extract the defineUnit call and requestUnits parameters
        const defineUnitMatch = callback.match(/cmWrapper\.ads\.defineUnit\(["']([^"']+)["']\)/);
        const requestUnitsMatch = callback.match(/cmWrapper\.ads\.requestUnits\((\[.*?\])\)/);
        
        if (!defineUnitMatch || !requestUnitsMatch) {
            // If we can't parse the callback properly, return it as-is
            return callback;
        }

        const unitId = defineUnitMatch[1];
        const requestUnitsParams = requestUnitsMatch[1];

        // Store the request units parameters (they should be the same for all units)
        if (!window.cordlessMediaManager.requestUnitsParams) {
            window.cordlessMediaManager.requestUnitsParams = requestUnitsParams;
        }

        // Add this unit to pending units
        window.cordlessMediaManager.pendingUnits.add(unitId);

        // Create a completely new callback that safely handles the unit definition
        // This replaces the entire original callback to avoid syntax issues
        const modifiedCallback = `
            (function() {
                function waitForCmWrapper(callback, maxAttempts = 50) {
                    if (window.cmWrapper && window.cmWrapper.ads && typeof window.cmWrapper.ads.defineUnit === 'function') {
                        callback();
                    } else if (maxAttempts > 0) {
                        setTimeout(function() {
                            waitForCmWrapper(callback, maxAttempts - 1);
                        }, 100);
                    }
                }
                
                waitForCmWrapper(function() {
                    try {
                        window.cmWrapper.ads.defineUnit("${unitId}");
                    } catch (error) {
                        console.error('AdSlot: Error defining unit ${unitId}:', error);
                    }
                });
            })();
        `;

        // Schedule requestUnits call to run after a short delay to allow all units to be defined
        this.scheduleRequestUnits();

        return modifiedCallback;
    }

    // Initialize Cordless Media Manager (singleton pattern)
    initializeCordlessMediaManager() {
        if (!window.cordlessMediaManager) {
            // Clean up any duplicate scripts before initializing
            AdSlot.cleanupDuplicateCordlessScripts();
            
            window.cordlessMediaManager = {
                pendingUnits: new Set(),
                processedUnits: new Set(),
                requestUnitsScheduled: false,
                requestUnitsTimeout: null,
                requestUnitsParams: null,
                initialized: true
            };
        }
    }

    // Schedule a single requestUnits call after all units are defined
    scheduleRequestUnits() {
        const manager = window.cordlessMediaManager;
        
        // If already scheduled, don't schedule again
        if (manager.requestUnitsScheduled) {
            return;
        }
        
        // Clear any existing timeout
        if (manager.requestUnitsTimeout) {
            clearTimeout(manager.requestUnitsTimeout);
        }

        // Schedule requestUnits to run after a short delay
        manager.requestUnitsTimeout = setTimeout(() => {
            // Double-check that we haven't already scheduled this
            if (manager.requestUnitsScheduled) {
                return;
            }
            
            // Initialize processedUnits set if it doesn't exist
            if (!manager.processedUnits) {
                manager.processedUnits = new Set();
            }
            
            // Filter out units that have already been processed
            const unprocessedUnits = Array.from(manager.pendingUnits).filter(unit => !manager.processedUnits.has(unit));
            
            if (unprocessedUnits.length > 0) {
                manager.requestUnitsScheduled = true;
                
                // Mark these units as processed
                unprocessedUnits.forEach(unit => manager.processedUnits.add(unit));
                
                // Remove any existing requestUnits script to prevent duplicates
                const existingRequestScript = document.querySelector('script[data-cordless-request-units="true"]');
                if (existingRequestScript) {
                    existingRequestScript.remove();
                }
                
                // Create and inject a single script to call requestUnits
                const requestUnitsScript = document.createElement('script');
                requestUnitsScript.type = 'text/javascript';
                requestUnitsScript.setAttribute('data-cordless-request-units', 'true');
                
                const scriptContent = `
                    if (window.cmWrapper && window.cmWrapper.ads && typeof window.cmWrapper.ads.requestUnits === 'function') {
                        try {
                            window.cmWrapper.ads.requestUnits(${manager.requestUnitsParams || '[]'});
                            
                            // Reset the scheduled flag after successful execution to allow future calls
                            if (window.cordlessMediaManager) {
                                setTimeout(function() {
                                    window.cordlessMediaManager.requestUnitsScheduled = false;
                                }, 1000);
                            }
                        } catch (error) {
                            console.error('Cordless Media: Error calling requestUnits:', error);
                            // Reset flag even on error to allow retry
                            if (window.cordlessMediaManager) {
                                window.cordlessMediaManager.requestUnitsScheduled = false;
                            }
                        }
                    } else {
                        console.error('Cordless Media: cmWrapper.ads.requestUnits not available');
                        // Reset flag if wrapper not available
                        if (window.cordlessMediaManager) {
                            window.cordlessMediaManager.requestUnitsScheduled = false;
                        }
                    }
                `;
                
                requestUnitsScript.text = scriptContent;
                document.head.appendChild(requestUnitsScript);
            }
        }, 300); // Increased delay to 300ms to ensure all units are properly defined
    }

    // Process callback template with actual targeting data
    processCallbackTemplate(callback) {
        let processed = callback;

        // Get targeting data from various sources
        const targetingData = this.getTargetingData();

        // Replace template variables
        processed = processed.replace(/#city#/g, targetingData.city || '');
        processed = processed.replace(/#state#/g, targetingData.state || '');
        processed = processed.replace(/#zip#/g, targetingData.zip || '');
        processed = processed.replace(/#address#/g, targetingData.address || '');
        processed = processed.replace(/#price#/g, targetingData.price || '');
        
        return processed;
    }

    // Get targeting data from available sources
    getTargetingData() {
        // Priority: window.searchLocation > store > property data
        if (window.searchLocation) {
            return window.searchLocation;
        }
      
        if (window.prop_json) {
            return {
                city: window.prop_json.city || '',
                state: window.prop_json.state || '',
                zip: window.prop_json.zip || '',
                address: window.prop_json.street_address || window.prop_json.city || '',
                price: window.prop_json.listPrice || ''
            };
        }
        
        if (window.store && window.store.state && window.store.state.search && window.store.state.search.location) {
            const searchLocation = window.store.state.search.location;
            const isZipSearch = searchLocation.type && searchLocation.type.toLowerCase() === 'zip';
            
            return {
                state: searchLocation.state || '',
                zip: isZipSearch ? searchLocation.query : searchLocation.zip || '',
                address: searchLocation.query || '',
                price: ''
            };
        }

        if (window.store && window.store.state && window.store.state.search && window.store.state.search.location) {
            const searchLocation = window.store.state.search.location;
            const isZipSearch = searchLocation.type && searchLocation.type.toLowerCase() === 'zip';
            
            return {
                city: searchLocation.city || '',
                state: searchLocation.state || '',
                zip: isZipSearch ? searchLocation.query : searchLocation.zip || '',
                address: searchLocation.query || '',
                price: ''
            };
        }

        return {};
    }

    // Destroy the slot and clean up
    destroy() {
        // Reset loaded state
        this.loaded = false;

        // Remove callback scripts (both regular and Cordless queue scripts)
        const existingScripts = document.querySelectorAll(`script[data-ad-slot="${this.id}"]`);
        existingScripts.forEach(script => {
            script.remove();
        });

        // Clear element content
        if (this.element && document.body.contains(this.element)) {
            this.element.innerHTML = '';
        }

        // Clean up callback injection flag
        const callbackKey = `adCallbackInjected_${this.id}`;
        if (window[callbackKey]) {
            delete window[callbackKey];
        }

        // Clean up Cordless Media manager tracking for this unit
        if (window.cordlessMediaManager && window.cordlessMediaManager.pendingUnits) {
            window.cordlessMediaManager.pendingUnits.delete(this.id);
            // Also remove from processed units
            if (window.cordlessMediaManager.processedUnits) {
                window.cordlessMediaManager.processedUnits.delete(this.id);
            }
            
            // If this was the last pending unit and we haven't called requestUnits yet, reset the scheduled flag
            // This allows new units to be processed after pagination
            if (window.cordlessMediaManager.pendingUnits.size === 0 && window.cordlessMediaManager.requestUnitsScheduled) {
                window.cordlessMediaManager.requestUnitsScheduled = false;
            }
        }

        // Try to destroy ad unit in external systems
        this.destroyExternalAdUnit();

        // Clear ad data
        this.adData = null;
    }

    // Attempt to destroy ad unit in external ad systems
    destroyExternalAdUnit() {
        // Cordless Media cleanup
        if (window.cmWrapper && window.cmWrapper.ads && typeof window.cmWrapper.ads.destroyUnit === 'function') {
            try {
                window.cmWrapper.ads.destroyUnit(this.id);
            } catch (e) {
                console.warn(`AdSlot: Failed to destroy Cordless ad unit for ${this.id}:`, e);
            }
        }

        // Google Ad Manager cleanup
        if (window.googletag && window.googletag.pubads && typeof window.googletag.destroySlots === 'function') {
            try {
                const slot = window.googletag
                    .pubads()
                    .getSlots()
                    .find((s) => s.getSlotElementId() === this.id);
                if (slot) {
                    window.googletag.destroySlots([slot]);
                }
            } catch (e) {
                console.warn(`AdSlot: Failed to destroy GPT slot for ${this.id}:`, e);
            }
        }
    }

    // Reload the ad (useful for refresh scenarios)
    async reload() {
        this.loaded = false;
        await this.load();
    }
    
    // Check if this slot can be loaded (has data but isn't loaded yet)
    canBeLoaded() {
        return !this.loaded && this.findAdData() !== null;
    }

    // Static helper method to reset Cordless Media manager (useful for page navigation)
    static resetCordlessMediaManager() {
        if (window.cordlessMediaManager) {
            if (window.cordlessMediaManager.requestUnitsTimeout) {
                clearTimeout(window.cordlessMediaManager.requestUnitsTimeout);
            }
            
            // Remove the global requestUnits script if it exists
            const existingRequestScript = document.querySelector('script[data-cordless-request-units="true"]');
            if (existingRequestScript) {
                existingRequestScript.remove();
            }
            
            // Reset the manager
            window.cordlessMediaManager = {
                pendingUnits: new Set(),
                processedUnits: new Set(),
                requestUnitsScheduled: false,
                requestUnitsTimeout: null,
                requestUnitsParams: null,
                initialized: true
            };
        }
    }

    // Static helper method to clean up duplicate Cordless Media scripts
    static cleanupDuplicateCordlessScripts() {
        const cordlessScripts = document.querySelectorAll('script[src*="cm.min.js"]');
        
        if (cordlessScripts.length > 1) {
            // Keep only the first one (or the one marked as main)
            let mainScript = document.querySelector('script[data-cordless-main="true"]');
            if (!mainScript) {
                mainScript = cordlessScripts[0]; // Use first one as main
                mainScript.setAttribute('data-cordless-main', 'true');
            }
            
            // Remove all others
            cordlessScripts.forEach((script, index) => {
                if (script !== mainScript) {
                    script.remove();
                }
            });
        }
    }
}
